import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { pushNotification } from '@/lib/notifications';
import { useAuth } from '@/hooks/useAuth';
import {
  uploadMessageAttachment,
  isImageAttachment,
  attachmentIcon,
  MAX_ATTACHMENT_BYTES,
  type MessageAttachment,
} from '@/lib/messageAttachment';
import AttachmentViewer, { type ViewerAttachment } from '@/components/feature/AttachmentViewer';

export interface ClientMsgRow {
  id: number;
  sender_id: number;
  receiver_id: number;
  message: string;
  created_at: string;
  is_read: number | null;
  attachment_url?: string | null;
  attachment_name?: string | null;
  attachment_type?: string | null;
}

interface Props {
  userId: number;
  merchantId: number;
  merchantName: string;
  merchantImage?: string | null;
  onBack: () => void;
}

/**
 * Fil de messages privés d'un client avec une boutique (côté espace client).
 * Le client peut répondre et joindre une photo ou un document.
 */
export default function ClientPrivateThread({
  userId, merchantId, merchantName, merchantImage, onBack,
}: Props) {
  const { user: authUser } = useAuth();
  const senderName = authUser?.name || authUser?.user_name || 'Client';
  const [messages, setMessages] = useState<ClientMsgRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [attachment, setAttachment] = useState<MessageAttachment | null>(null);
  const [uploading, setUploading] = useState(false);
  const [viewer, setViewer] = useState<ViewerAttachment | null>(null);
  const endRef = useRef<HTMLDivElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: err } = await supabase
        .from('messages')
        .select('id, sender_id, receiver_id, message, created_at, is_read, attachment_url, attachment_name, attachment_type')
        .or(
          `and(sender_id.eq.${userId},receiver_id.eq.${merchantId}),and(sender_id.eq.${merchantId},receiver_id.eq.${userId})`,
        )
        .order('created_at', { ascending: true })
        .limit(500);
      if (err) throw err;
      setMessages((data || []) as ClientMsgRow[]);

      // Marque comme lus les messages entrants du marchand.
      const unread = ((data || []) as ClientMsgRow[]).filter((m) => m.receiver_id === userId && !m.is_read);
      if (unread.length > 0) {
        void supabase.from('messages').update({ is_read: 1 }).in('id', unread.map((m) => m.id)).then(() => {});
      }
    } catch {
      setError('Impossible de charger la conversation.');
    } finally {
      setLoading(false);
    }
  }, [userId, merchantId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages.length]);

  const pickFile = async (file: File | undefined) => {
    if (!file) return;
    setError(null);
    if (file.size > MAX_ATTACHMENT_BYTES) {
      setError('Fichier trop volumineux (10 Mo maximum).');
      if (fileRef.current) fileRef.current.value = '';
      return;
    }
    setUploading(true);
    try {
      setAttachment(await uploadMessageAttachment(file, userId));
    } catch {
      setError("Impossible de joindre le fichier. Réessayez.");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const send = async () => {
    const text = input.trim();
    if ((!text && !attachment) || sending || uploading) return;
    setSending(true);
    setError(null);
    try {
      const { data, error: err } = await supabase
        .from('messages')
        .insert({
          sender_id: userId,
          receiver_id: merchantId,
          message: text,
          is_read: 0,
          attachment_url: attachment?.url ?? null,
          attachment_name: attachment?.name ?? null,
          attachment_type: attachment?.type ?? null,
        })
        .select('id, sender_id, receiver_id, message, created_at, is_read, attachment_url, attachment_name, attachment_type')
        .maybeSingle();
      if (err) throw err;
      if (data) setMessages((prev) => [...prev, data as ClientMsgRow]);
      setInput('');
      setAttachment(null);

      // 🔔 Cloche du marchand : nouveau message privé reçu (avec le nom de l'expéditeur).
      void pushNotification({
        userId: merchantId,
        senderId: userId,
        publicationId: userId,
        type: 'message',
        message: text
          ? `Nouveau message de ${senderName} : ${text.slice(0, 120)}`
          : `Nouveau message de ${senderName} (pièce jointe : ${attachment?.name || 'fichier'})`,
      });

      try {
        await supabase.from('core_notifications').insert({
          channel: 'in_app',
          type: 'client_private_message',
          title: 'Nouveau message privé d\'un client',
          body: (text || attachment?.name || 'Pièce jointe').slice(0, 300),
          entity_type: 'client_message',
          entity_id: String(userId),
          recipient_user_id: merchantId,
          status: 'pending',
          metadata: { client_user_id: userId },
        });
      } catch { /* best-effort */ }
    } catch {
      setError("L'envoi a échoué. Réessayez.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="flex flex-col flex-1 min-w-0 bg-background-50 border border-background-200/70 rounded-lg overflow-hidden h-[calc(100vh-260px)] min-h-[420px]">
      <div className="flex items-center gap-3 px-4 py-3 border-b border-background-200/70 bg-background-100/50 flex-shrink-0">
        <button
          type="button"
          onClick={onBack}
          className="lg:hidden w-8 h-8 flex items-center justify-center rounded-full text-foreground-600 hover:bg-background-200/60 cursor-pointer flex-shrink-0"
          aria-label="Retour"
        >
          <i className="ri-arrow-left-line"></i>
        </button>
        <div className="w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center overflow-hidden flex-shrink-0">
          {merchantImage ? (
            <img src={merchantImage} alt="" className="w-full h-full object-cover" />
          ) : (
            <span className="text-sm font-bold text-accent-700">{merchantName.charAt(0).toUpperCase()}</span>
          )}
        </div>
        <div className="min-w-0">
          <p className="text-sm font-semibold text-foreground-900 truncate">{merchantName}</p>
          <p className="text-[11px] text-foreground-500">Boutique</p>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-2.5">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <i className="ri-loader-4-line animate-spin text-2xl text-accent-500"></i>
          </div>
        ) : messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center text-foreground-400">
            <i className="ri-chat-3-line text-3xl mb-2"></i>
            <p className="text-sm">Écrivez votre premier message à {merchantName}</p>
          </div>
        ) : (
          messages.map((m) => {
            const mine = m.sender_id === userId;
            const isImg = isImageAttachment(m.attachment_type, m.attachment_name);
            return (
              <div key={m.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[80%] px-3.5 py-2 rounded-lg text-sm ${mine ? 'bg-accent-100/70 text-foreground-800' : 'bg-background-100 text-foreground-800'}`}>
                  {m.message && <p style={{ whiteSpace: 'pre-wrap' }}>{m.message}</p>}
                  {m.attachment_url && (
                    isImg ? (
                      <button
                        type="button"
                        onClick={() => setViewer({ url: m.attachment_url as string, name: m.attachment_name, type: m.attachment_type })}
                        className={`block cursor-pointer ${m.message ? 'mt-1.5' : ''}`}
                        aria-label={`Ouvrir ${m.attachment_name || 'la pièce jointe'}`}
                      >
                        <img src={m.attachment_url} alt={m.attachment_name || ''} className="max-w-[220px] max-h-[220px] rounded-md object-cover" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setViewer({ url: m.attachment_url as string, name: m.attachment_name, type: m.attachment_type })}
                        className={`flex items-center gap-2.5 px-2.5 py-2 rounded-md bg-background-50 border border-background-200/70 mt-1 hover:bg-background-100 transition-colors cursor-pointer text-left w-full ${m.message ? 'mt-1.5' : ''}`}
                      >
                        <span className="w-8 h-8 rounded-md bg-secondary-100 text-secondary-900 flex items-center justify-center flex-shrink-0">
                          <i className={attachmentIcon(m.attachment_type, m.attachment_name)}></i>
                        </span>
                        <span className="min-w-0">
                          <span className="block text-xs font-medium text-foreground-800 truncate max-w-[150px]">{m.attachment_name || 'Document'}</span>
                          <span className="block text-[10px] text-foreground-400">Aperçu dans Zifek</span>
                        </span>
                      </button>
                    )
                  )}
                  <p className="text-[10px] text-foreground-400 mt-1">
                    {new Date(m.created_at).toLocaleString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
              </div>
            );
          })
        )}
        <div ref={endRef} />
      </div>

      <div className="p-3 border-t border-background-200/70 flex-shrink-0">
        {error && <p className="text-xs text-red-500 mb-2">{error}</p>}
        {attachment && (
          <div className="flex items-center gap-2 mb-2 px-2.5 py-1.5 rounded-lg bg-background-100 border border-background-200/70 w-fit max-w-full">
            <i className={`${attachmentIcon(attachment.type, attachment.name)} text-accent-600`}></i>
            <span className="text-xs text-foreground-700 truncate max-w-[200px]">{attachment.name}</span>
            <button type="button" onClick={() => setAttachment(null)} className="w-5 h-5 flex items-center justify-center rounded-full text-foreground-400 hover:text-red-500 cursor-pointer flex-shrink-0" aria-label="Retirer">
              <i className="ri-close-line text-xs"></i>
            </button>
          </div>
        )}
        <div className="flex items-center gap-2">
          <input
            ref={fileRef}
            type="file"
            accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.zip"
            className="hidden"
            onChange={(e) => pickFile(e.target.files?.[0])}
          />
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={uploading || sending}
            className="w-10 h-10 rounded-full border border-background-200/70 text-foreground-600 flex items-center justify-center cursor-pointer hover:bg-background-100 disabled:opacity-50 transition-colors flex-shrink-0"
            title="Joindre une photo ou un document"
          >
            <i className={uploading ? 'ri-loader-4-line animate-spin' : 'ri-attachment-2'}></i>
          </button>
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') send(); }}
            placeholder={`Message à ${merchantName}...`}
            className="flex-1 px-4 py-2.5 bg-background-50 border border-background-200/70 rounded-full text-sm focus:outline-none focus:border-accent-300"
          />
          <button
            type="button"
            onClick={send}
            disabled={(!input.trim() && !attachment) || sending || uploading}
            className="w-10 h-10 rounded-full bg-accent-500 text-background-50 flex items-center justify-center cursor-pointer hover:bg-accent-600 disabled:opacity-50 transition-colors flex-shrink-0"
            aria-label="Envoyer"
          >
            <i className={sending ? 'ri-loader-4-line animate-spin' : 'ri-send-plane-fill'}></i>
          </button>
        </div>
      </div>
      {viewer && <AttachmentViewer attachment={viewer} onClose={() => setViewer(null)} />}
    </div>
  );
}